import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-carousel>` says about itself.
 *
 * The distinction this piece is most often on the wrong side of is with CardGrid. A card grid shows
 * a set ALL AT ONCE so a reader can compare it; a carousel shows one thing at a time and moves
 * between them. If the point is "see these together", a grid is the answer and a carousel actively
 * hides four fifths of it.
 *
 * ON THE IMAGES. An image is not a component, and there is no `caos-carousel-image` to place. The
 * images are the `images` input — `{ src, alt, heading?, description?, href? }` — the same fold the
 * accordion applies to a section and the menu to an item. A page author never places an image; they
 * configure a carousel.
 *
 * ON AUTOPLAY. It is off unless the author asks for it, and every part of it is declared rather than
 * baked in: whether it runs, how long a slide holds, and whether hovering stops it. What is NOT
 * optional is the reduced-motion behaviour — a reader who has asked for less motion gets a still
 * gallery on its first slide, and no input turns that off.
 */
const docs: ComponentDocs = {
  aliases: ['slideshow', 'image gallery', 'rotator', 'slider', 'image carousel'],

  answers: [
    'show a run of pictures one at a time',
    'put a rotating gallery on a landing page',
    'step through images without a mouse',
    'stop a gallery moving for somebody who asked for less motion',
    'give each picture a heading and a link of its own',
  ],

  // A rotating gallery leads a page rather than organising a record: an app's home page, or a page
  // inside an app. The public marketing surfaces this piece was adopted for (CAOS-69) do not exist
  // yet, and the placement vocabulary has no member for them; the grant widens in the change that
  // builds that surface rather than here, on a metatype already live in orgs.
  placements: ['homePage', 'appPage'],

  inputs: [
    {
      name: 'images',
      // No attribute: a list of images cannot be written in one.
      type: 'json',
      description:
        'The images, as { src, alt, heading?, description?, href? } objects, in the order they are ' +
        'shown. src is the picture and alt is what it shows for somebody who cannot see it — an empty ' +
        'alt marks a picture that carries no meaning of its own. A heading and a description are drawn ' +
        'over the picture when they are given, and an href makes the whole slide a link. An entry with ' +
        'no src is dropped.',
    },
    {
      name: 'autoplay',
      attribute: 'autoplay',
      type: 'boolean',
      description:
        'Moves to the next image on a timer. Off unless set, and it never starts for a reader whose ' +
        'system asks for reduced motion, however this is set. The timer runs only while the carousel ' +
        'is on the page and there is more than one image to move between.',
      default: false,
    },
    {
      name: 'interval',
      attribute: 'interval',
      type: 'number',
      description:
        'How long each image holds before autoplay moves on, in milliseconds. Only read when autoplay ' +
        'is set. A value that is not a positive number leaves the carousel still rather than moving at ' +
        'some invented speed.',
      default: 5000,
    },
    {
      name: 'noPauseOnHover',
      attribute: 'no-pause-on-hover',
      type: 'boolean',
      description:
        'Keeps autoplay running while a reader is engaged. Left unset, pointing at the carousel or ' +
        'moving focus into it stops the timer, and leaving starts it again, because a gallery that ' +
        'keeps moving under somebody reading it is the complaint this exists to answer. Declared as a ' +
        'negative, like noBorder and noIcon, because a page writes a yes/no as present-or-absent and a ' +
        'positive input that was on by default would have no reachable way to turn it off.',
      default: false,
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What a screen reader calls this carousel, for a page holding more than one. Left unset it is ' +
        'announced as "Carousel", which is enough when there is only the one.',
    },
  ],

  events: [
    {
      name: 'caos-carousel-change',
      when:
        'The image shown has changed — a previous or next press, an arrow key, a dot, autoplay moving ' +
        'on, or a goTo call. Not raised when the carousel is first drawn, and not raised when a move ' +
        'lands on the image already shown.',
      carries: '{ index, count } — the image now shown, counting from zero, and how many there are.',
    },
  ],

  state: [
    { name: 'index', type: 'number', description: 'The image shown now, counting from zero.' },
    { name: 'count', type: 'number', description: 'How many images the carousel is holding.' },
    {
      name: 'playing',
      type: 'boolean',
      description:
        'Whether the autoplay timer is running now. False while it is paused for a hover, for focus, ' +
        'or for a reduced-motion preference, so it answers "is this moving" rather than "was it asked to".',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The ground behind a picture that has not loaded, and the caption band.' },
    { token: '--caos-color-surface-2', controls: 'The viewport behind the images.' },
    { token: '--caos-color-border', controls: 'The edge of the previous and next controls and of a dot.' },
    { token: '--caos-color-text', controls: 'The heading on a slide and the glyph on a control.' },
    { token: '--caos-color-text-muted', controls: 'The description under a heading.' },
    { token: '--caos-color-accent', controls: 'The dot marking the image being shown.' },
    { token: '--caos-font', controls: 'The typeface of the headings and descriptions.' },
    { token: '--caos-space-1', controls: 'The padding inside a previous or next control.' },
    { token: '--caos-space-2', controls: 'The gap between the viewport and the controls, and between the dots.' },
    { token: '--caos-space-3', controls: 'The padding inside the caption band.' },
    { token: '--caos-radius', controls: 'The corners of the viewport.' },
    { token: '--caos-radius-pill', controls: 'The shape of the previous and next controls and of a dot.' },
    { token: '--caos-state-hover-bg', controls: 'A previous or next control under the pointer.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a control reached by keyboard.' },
    { token: '--caos-duration-base', controls: 'How long one image takes to give way to the next.' },
    { token: '--caos-ease-standard', controls: 'The curve of that change.' },
  ],

  parts: [
    { part: 'carousel', is: 'the whole gallery, viewport and controls together' },
    { part: 'viewport', is: 'the window the images are shown in' },
    { part: 'slide', is: 'one image and its caption' },
    { part: 'image', is: 'the picture itself' },
    { part: 'caption', is: 'the band over a picture holding its heading and description', when: 'the image carries a heading or a description' },
    { part: 'heading', is: 'the line of display text on a slide', when: 'the image carries a heading' },
    { part: 'description', is: 'the sentence under a heading', when: 'the image carries a description' },
    { part: 'controls', is: 'the row holding the previous and next controls and the dots' },
    { part: 'previous', is: 'the control that goes back one image' },
    { part: 'next', is: 'the control that goes forward one image' },
    { part: 'dots', is: 'the run of marks, one per image' },
    { part: 'dot', is: 'one mark, which moves to the image it names when pressed' },
  ],

  methods: [
    {
      name: 'next',
      description: 'Move forward one image, wrapping from the last to the first. Returns false when nothing changed.',
      args: [],
    },
    {
      name: 'previous',
      description: 'Move back one image, wrapping from the first to the last. Returns false when nothing changed.',
      args: [],
    },
    {
      name: 'goTo',
      description:
        'Show a particular image. Returns false when nothing changed — the index is outside the list, or ' +
        'it is the image already shown.',
      args: [{ name: 'index', type: 'number', description: 'Which image to show, counting from zero.' }],
    },
    {
      name: 'play',
      description:
        'Start the autoplay timer, as setting the attribute does. It does nothing for a reader who has ' +
        'asked for reduced motion, which is the one thing no caller can override.',
      args: [],
    },
    { name: 'pause', description: 'Stop the autoplay timer, leaving the image that is showing on screen.', args: [] },
  ],

  placing:
    'Across the full width of the region it leads — a page section of one column, at the top of a home ' +
    'page or a landing page. It adds no outer margin, so it lines up with whatever stands above and ' +
    'below it, and the viewport keeps a 16:9 window whatever the pictures are, so slides of different ' +
    'sizes do not make the page jump as they change. In a section of two columns it takes one of them ' +
    'like any other piece, and the captions reflow with it.',

  whenToUse:
    'A short run of pictures that lead a page — four or five, each worth a moment on its own, where a ' +
    'reader is invited rather than asked to compare. If somebody needs to see them together, or to find ' +
    'a particular one, a grid is the honest answer: a carousel hides everything except the slide showing.',

  insteadUse: [
    {
      name: 'CardGrid',
      when: 'The reader needs to see the set at once and compare it, or to pick a particular one out of it.',
    },
    {
      name: 'TabStrip',
      when: 'There are a few named faces of one subject and the reader chooses between them, with nothing moving on its own.',
    },
  ],

  accessibility:
    'The carousel is a region announced as a carousel, and each slide is a group labelled with its ' +
    'position, so a screen reader says which of how many is showing. The slides not showing are hidden ' +
    'rather than merely out of sight, so they are out of the tab order and the accessibility tree and a ' +
    'reader never tabs into a picture nobody can see. Previous and next are real buttons, each dot is a ' +
    'button naming the image it goes to, and Arrow Left and Arrow Right move between images while Home ' +
    'and End go to the first and the last. A polite status region reports the new position on a move ' +
    'somebody made, and is silenced while autoplay is running so a reader is not interrupted every few ' +
    'seconds by motion they did not ask for. Reduced motion is honoured in behaviour and not only in ' +
    'paint: autoplay does not start, the gallery rests on the first image, and moving by hand still ' +
    'works. What is left to the author: an alt for every picture that carries meaning, and an empty one ' +
    'for every picture that does not.',

  examples: [
    {
      key: 'basic',
      label: 'A run of pictures',
      summary:
        'The ordinary case: three pictures, moved by the controls or the arrow keys. Nothing moves on its own, because autoplay was not asked for.',
      placement: {
        component: 'caos-carousel',
        inputs: {
          label: 'Platform highlights',
          images: [
            { src: 'https://images.example.com/workspace.jpg', alt: 'A workspace with several records open side by side' },
            { src: 'https://images.example.com/builder.jpg', alt: 'A page being assembled from the component palette' },
            { src: 'https://images.example.com/community.jpg', alt: 'People at a community meet-up around a long table' },
          ],
        },
      },
    },
    {
      key: 'autoplay',
      label: 'Moving on a timer',
      summary:
        'Autoplay set with a four-second hold. Pointing at it or moving focus into it stops the timer, and leaving starts it again.',
      placement: {
        component: 'caos-carousel',
        inputs: {
          label: 'What the platform does',
          autoplay: true,
          interval: 4000,
          images: [
            { src: 'https://images.example.com/workspace.jpg', alt: 'A workspace with several records open side by side' },
            { src: 'https://images.example.com/builder.jpg', alt: 'A page being assembled from the component palette' },
            { src: 'https://images.example.com/academy.jpg', alt: 'A lesson page open at a short exercise' },
          ],
        },
      },
    },
    {
      key: 'reduced-motion',
      label: 'The same gallery, reduced motion',
      summary:
        'The same element under a reduced-motion preference — the timer never starts and the gallery rests on the first picture. Turn the setting on and this is what the example above becomes.',
      placement: {
        component: 'caos-carousel',
        inputs: {
          label: 'What the platform does',
          autoplay: true,
          interval: 4000,
          images: [
            { src: 'https://images.example.com/workspace.jpg', alt: 'A workspace with several records open side by side' },
            { src: 'https://images.example.com/builder.jpg', alt: 'A page being assembled from the component palette' },
            { src: 'https://images.example.com/academy.jpg', alt: 'A lesson page open at a short exercise' },
          ],
        },
      },
    },
    {
      key: 'captioned',
      label: 'Headings, descriptions and links',
      summary:
        'Each picture carrying a heading, a sentence under it, and a link, so a slide leads somewhere instead of only being looked at.',
      placement: {
        component: 'caos-carousel',
        inputs: {
          label: 'Where to start',
          images: [
            {
              src: 'https://images.example.com/academy.jpg',
              alt: 'A lesson page open at a short exercise',
              heading: 'Learn the platform',
              description: 'Short lessons that build one working app end to end.',
              href: '/academy',
            },
            {
              src: 'https://images.example.com/community.jpg',
              alt: 'People at a community meet-up around a long table',
              heading: 'Meet the community',
              description: 'Questions answered by the people who build on this every day.',
              href: '/community',
            },
            {
              src: 'https://images.example.com/careers.jpg',
              alt: 'Two colleagues talking at a desk by a window',
              heading: 'Work with us',
              description: 'The roles open right now, and what each team is building.',
              href: '/careers',
            },
          ],
        },
      },
    },
  ],
};

export default docs;
